import React, { useState, useEffect } from 'react';
import { X, Search, Check, CheckSquare, Square, Eye, ArrowLeft, Printer } from 'lucide-react';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import QRCode from 'qrcode';
import qrImage from "../../Assets/qr-code.png";
import DocMasterService from '../../Api/DocMasterService';
import { toast } from 'react-toastify';
import '../../Style/Print.css';
import { useHistoryList } from '../Breadcrum/HistoryProvider';
import Breadcrumbs from '../Breadcrum/BreadcrumbHistory';
import { ServiceProvider } from '../../Api/ServiceProvider';
import SecureImage from '../ImgService/loadImage';

const PrintScreen = () => {
    const [count, setCount] = useState(1);
    const [docMasters, setDocMasters] = useState([]);
    const [pdiItems, setPdiItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showPrint, setShowPrint] = useState(null);
    const [showBulkPrint, setShowBulkPrint] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState('docmaster');
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [userData, setUserData] = useState(null);
    const [previewTotal, setPreviewTotal] = useState(0);
    const [showBoxSelection, setShowBoxSelection] = useState(false);
    const [boxSelectionItem, setBoxSelectionItem] = useState(null);
    const [selectedBoxTypes, setSelectedBoxTypes] = useState([]);
    const [boxSelectionCount, setBoxSelectionCount] = useState(1);
    // New state for action button single item print with custom distribution
    const [actionItem, setActionItem] = useState(null);
    const [actionBoxDistribution, setActionBoxDistribution] = useState(null);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user"));
        setUserData(user);
    }, []);

    const calculateBoxDistribution = (totalQty, boxQty) => {
        if (!boxQty || boxQty <= 0) boxQty = totalQty;
        const distribution = [];
        let remaining = parseInt(totalQty) || 0;
        const boxSize = parseInt(boxQty) || remaining;

        const fullBoxes = Math.floor(remaining / boxSize);
        const lastBoxQty = remaining % boxSize;

        if (fullBoxes > 0) {
            distribution.push({
                id: 'full_boxes',
                type: 'full',
                qty: boxSize,
                count: fullBoxes,
                description: `${boxSize} pcs × ${fullBoxes} boxes`
            });
        }

        if (lastBoxQty > 0) {
            distribution.push({
                id: 'remaining',
                type: 'remaining',
                qty: lastBoxQty,
                count: 1,
                description: `${lastBoxQty} pcs (remaining)`
            });
        }

        return distribution;
    };

    const calculateTotalStickers = (items, copies = 1) => {
        let total = 0;
        items.forEach(item => {
            const qty = parseInt(item.qty) || 0;
            const boxQty = parseInt(item.b_qty) || qty;
            const boxesNeeded = Math.ceil(qty / boxQty);
            total += boxesNeeded * copies;
        });
        return total;
    };

    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            toast.info("Please enter a search term", { autoClose: 3000 });
            return;
        }
        if (searchTerm.trim().length < 3) {
            toast.error("Please enter at least 3 characters", { autoClose: 3000 });
            return;
        }

        setLoading(true);
        setCurrentPage(1);
        try {
            const response = await DocMasterService.getAllPrint(1, 1000, searchTerm);

            if (response.success && response.data && response.data.length > 0) {
                const filteredData = response.data.map(doc => ({
                    ...doc,
                    pdi_items: doc.pdi_items?.filter(item => item.qr_m === true) || []
                }));

                setDocMasters(filteredData);
                toast.success(`Found ${response.data.length} documents`, { autoClose: 3000 });
            } else {
                setDocMasters([]);
                toast.info("No records found", { autoClose: 3000 });
            }
        } catch (err) {
            console.error(err);
            toast.error(String(err));
            setDocMasters([]);
        } finally {
            setLoading(false);
        }
    };

    const clearSearch = () => {
        setSearchTerm('');
        setDocMasters([]);
        setCurrentPage(1);
    };

    const handleViewPDI = (doc) => {
        if (!doc.pdi_items || doc.pdi_items.length === 0) {
            toast.error("No PDI items available for this document", { autoClose: 3000 });
            return;
        }

        const itemsWithIds = doc.pdi_items.map(item => ({
            ...item,
            id: item._id || `${doc.tpl_no}_${item.pn}_${item.sz}`,
            tpl_no: doc.tpl_no
        }));

        itemsWithIds.sort((a, b) => (a.sn || 0) - (b.sn || 0));

        setPdiItems(itemsWithIds);
        setSelectedDoc(doc);
        addCustomPath(doc?.tpl_no);
        setSelectedItems([]);
        setSelectAll(false);
        setViewMode('pdi');
        setCurrentPage(1);
    };

    const handleBackToList = () => {
        setViewMode('docmaster');
        setSelectedDoc(null);
        setPdiItems([]);
        setSelectedItems([]);
        setSelectAll(false);
    };

    // FIXED: Round-robin printing with proper handling for custom distribution
    const generateAndPrintQRCards = async (items, copies = 1, boxDistribution = null) => {
         try {
            if (!items || items.length === 0) {
                toast.error("No items selected for printing", { autoClose: 3000 });
                return;
            }

            const sortedItems = [...items].sort((a, b) => (a.sn || 0) - (b.sn || 0));

            console.log("Generating stickers:");
            console.log("Items:", sortedItems.map(i => ({ pn: i.pn, sn: i.sn })));
            console.log("Copies:", copies);
            console.log("Box Distribution:", boxDistribution);

            const heightMm = 40;
            const mmToPixel = (mm) => Math.round(mm * 96 / 25.4);
            const qrSize = Math.round(mmToPixel(heightMm) * 0.55);
            const cards = [];

            for (let c = 0; c < copies; c++) {
                console.log(`Generating copy ${c + 1} of ${copies}`);

                for (const item of sortedItems) {
                    let distribution = [];

                    // Check if custom distribution is provided for this specific item
                    if (boxDistribution && boxDistribution.length > 0) {
                        const distArray = Array.isArray(boxDistribution) ? boxDistribution : [boxDistribution];

                        // Check if this distribution is for the current item
                        const itemDist = distArray.filter(box =>
                            box._itemId === item.id && box.selected !== false
                        );

                        if (itemDist.length > 0) {
                            // Use custom distribution
                            distribution = itemDist.map(box => ({
                                qty: box.qty,
                                count: box.count || 1
                            }));
                            console.log(`Using custom distribution for item ${item.pn}:`, distribution);
                        } else if (distArray[0]?._itemId === item.id || !distArray[0]?._itemId) {
                            // Distribution provided but no specific item match, use as-is if selected
                            distribution = distArray
                                .filter(box => box.selected !== false)
                                .map(box => ({
                                    qty: box.qty,
                                    count: box.count || 1
                                }));
                            console.log(`Using provided distribution:`, distribution);
                        }
                    }

                    // If no custom distribution, calculate default
                    if (distribution.length === 0) {
                        const totalQty = parseInt(item.qty) || 0;
                        const boxQty = parseInt(item.b_qty) || totalQty;
                        const defaultDist = calculateBoxDistribution(totalQty, boxQty);
                        distribution = defaultDist.map(box => ({
                            qty: box.qty,
                            count: box.count
                        }));
                        console.log(`Using default distribution for item ${item.pn}:`, distribution);
                    }

                    // Generate cards for each distribution box
                    for (const dist of distribution) {
                        for (let i = 0; i < dist.count; i++) {
                            const qrData = {
                                pn: (item.pn || "").trim(),
                                qty: dist.qty
                            };
                            const qrText = JSON.stringify(qrData);
                            const descLines = (item.desc || "").split('\n');

                            const canvas = document.createElement("canvas");
                            await QRCode.toCanvas(canvas, qrText, {
                                errorCorrectionLevel: "L",
                                margin: 1,
                                width: qrSize,
                            });
                            const qrSrc = canvas.toDataURL("image/png");

                            const descHtml = descLines.map(line =>
                                `<div class="desc-line">${line}</div>`
                            ).join("");

                            cards.push(`
                                <div class="qr-preview-card">
                                    <div class="card-top-section">
                                        <img src="${qrSrc}" class="qr-image" />
                                        <div class="text-content">
                                            <div class="info-line">
                                                <span class="label">TPL No:</span>
                                                <span class="value pn-value">${selectedDoc.tpl_no || ""}</span>
                                            </div>
                                            <div class="info-line">
                                                <span class="label">Part No:</span>
                                                <span class="value pn-value">${item.pn || ""}</span>
                                            </div>
                                            <div class="info-line">
                                                <span class="label">Size:</span>
                                                <span class="value">${item.sz || ""}</span>
                                            </div>
                                            <div class="info-line">
                                                <span class="label">Quantity:</span>
                                                <span class="value">${dist.qty}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="desc-block">
                                        ${descHtml}
                                    </div>
                                </div>
                            `);
                        }
                    }
                }
            }

            console.log(`Total cards generated: ${cards.length}`);

            const printWindow = window.open("", "_blank");
            printWindow.document.write(`
                <html>
                <head>
                <title>Print QR Cards</title>
                <style>
                    @page {
                        size: 75mm 40mm;
                        margin: 0;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        font-family: Arial, sans-serif;
                        background: white;
                    }
                    .qr-preview-card {
                        width: 75mm;
                        height: 40mm;
                        padding: 2.5mm;
                        box-sizing: border-box;
                        background: #f8f9fa;
                        border: 1px solid #e0e0e0;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        page-break-after: always;
                    }
                    .qr-preview-card:last-child {
                        page-break-after: auto;
                    }
                    .card-top-section {
                        display: flex;
                        align-items: flex-start;
                    }
                    .qr-image {
                        width: ${qrSize}px;
                        height: ${qrSize}px;
                        background: white;
                        flex-shrink: 0;
                        margin-right: 3mm;
                    }
                    .text-content {
                        flex: 1;
                        min-width: 0;
                    }
                    .info-line {
                        display: flex;
                        align-items: baseline;
                        margin-bottom: 1mm;
                        margin-top: 1.5mm;
                        font-size: 10.5px;
                        line-height: 1.2;
                    }
                    .info-line:last-child {
                        margin-bottom: 0;
                    }
                    .label {
                        font-weight: bold;
                        color: #000;
                        flex-shrink: 0;
                        text-align: left;
                        margin-right: 1mm;
                    }
                    .value {
                        color: #000;
                        flex: 1;
                        text-align: left;
                        font-weight: bold;
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                    }
                    .pn-value {
                        white-space: normal;
                        word-break: break-all;
                        max-width: 100%;
                    }
                    .desc-block {
                        margin-top: 2mm;
                        padding-top: 1mm;
                        border-top: 1px dashed #ccc;
                    }
                    .desc-line {
                        font-size: 11px;
                        line-height: 1.2;
                        color: #000;
                        font-weight: bold;
                    }
                </style>
                </head>
                <body>
                    ${cards.join("")}
                    <script>
                        window.onload = () => {
                            console.log("Printing ${cards.length} stickers");
                            window.print();
                            setTimeout(() => window.close(), 1000);
                        }
                    </script>
                </body>
                </html>
            `);

            printWindow.document.close();

            toast.success(`Generated ${cards.length} QR cards`, { autoClose: 3000 });

        } catch (e) {
            console.error(e);
            toast.error("Failed to print QR codes", { autoClose: 3000 });
        }
    };

    const handleSelectItem = (item) => {
        setSelectedItems(prev => {
            const isSelected = prev.some(selected => selected.id === item.id);
            if (isSelected) {
                return prev.filter(selected => selected.id !== item.id);
            } else {
                return [...prev, item];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedItems([]);
        } else {
            const sorted = [...pdiItems].sort((a, b) => (a.sn || 0) - (b.sn || 0));
            setSelectedItems(sorted);
        }
        setSelectAll(!selectAll);
    };

    const handleBulkPrint = () => {
        if (selectedItems.length === 0) {
            toast.error("Please select items to print", { autoClose: 3000 });
            return;
        }
        setCount(1);
        const total = calculateTotalStickers(selectedItems, 1);
        setPreviewTotal(total);
        setShowBulkPrint(true);
    };

    // UPDATED: Action button - Always show popup
    const handleActionPrint = (item) => {
        const qty = parseInt(item.qty) || 0;
        const boxQty = parseInt(item.b_qty) || qty;

        const isEvenlyDivisible = qty % boxQty === 0;

        if (!isEvenlyDivisible) {
            // Not evenly divisible - show box selection popup
            const distribution = calculateBoxDistribution(qty, boxQty);
            setSelectedBoxTypes(distribution.map(d => ({ ...d, selected: false, _itemId: item.id })));
            setBoxSelectionItem(item);
            setBoxSelectionCount(1);
            setShowBoxSelection(true);
        } else {
            // Evenly divisible - show action print popup with single box logic
            const singleBoxDist = [{
                id: 'single_box',
                type: 'full',
                qty: boxQty,
                count: 1,
                description: `${boxQty} pcs × 1 box`,
                selected: true,
                _itemId: item.id
            }];

            setActionItem(item);
            setActionBoxDistribution(singleBoxDist);
            setCount(1);
            setPreviewTotal(1);
            setShowBulkPrint(true);
        }
    };

    // Handle print from action button popup (evenly divisible case)
    const handleActionPrintExecute = () => {
        if (!actionItem || !actionBoxDistribution) return;

        generateAndPrintQRCards([actionItem], count, actionBoxDistribution);

        setShowBulkPrint(false);
        setActionItem(null);
        setActionBoxDistribution(null);
        setPreviewTotal(0);
        setCount(1);
    };

    const toggleBoxTypeSelection = (boxId) => {
        setSelectedBoxTypes(prev => prev.map(box => ({
            ...box,
            selected: box.id === boxId
        })));
    };

    const handleBoxSelectionCountChange = (newCount) => {
        const val = parseInt(newCount) || 1;
        if (val < 1) return;
        if (val > 100) return;
        setBoxSelectionCount(val);
    };

    const handlePrintFromBoxSelection = () => {
        const selectedBox = selectedBoxTypes.find(box => box.selected);
        if (!selectedBox) {
            toast.error("Please select one box type to print", { autoClose: 3000 });
            return;
        }

        // Create distribution with count
        const distribution = [{
            ...selectedBox,
            count: 1,
            _itemId: boxSelectionItem.id
        }];

        generateAndPrintQRCards([boxSelectionItem], boxSelectionCount, distribution);

        setShowBoxSelection(false);
        setBoxSelectionItem(null);
        setSelectedBoxTypes([]);
        setBoxSelectionCount(1);
    };

    const handleCountChange = (newCount) => {
        const val = parseInt(newCount) || 1;
        if (val < 1) return;
        if (val > 100) return;

        setCount(val);

        if (showPrint) {
            const total = calculateTotalStickers([showPrint], val);
            setPreviewTotal(total);
        } else if (showBulkPrint && selectedItems.length > 0) {
            const total = calculateTotalStickers(selectedItems, val);
            setPreviewTotal(total);
        } else if (showBulkPrint && actionItem) {
            setPreviewTotal(val);
        }
    };

    const handleExecuteBulkPrint = () => {
        if (selectedItems.length === 0) {
            toast.error("No items selected", { autoClose: 3000 });
            return;
        }

        generateAndPrintQRCards(selectedItems, count);

        setShowBulkPrint(false);
        setPreviewTotal(0);
        setCount(1);
    };

    const totalPages = viewMode === 'docmaster'
        ? Math.ceil(docMasters.length / itemsPerPage)
        : Math.ceil(pdiItems.length / itemsPerPage);

    const paginatedDocMasters = docMasters.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const paginatedPdiItems = pdiItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (pdiItems.length > 0) {
            const allSelected = pdiItems.every(item =>
                selectedItems.some(selected => selected.id === item.id)
            );
            setSelectAll(allSelected);
        } else {
            setSelectAll(false);
        }
    }, [selectedItems, pdiItems]);

    const { addCustomPath, removeCustomPath } = useHistoryList();

    const backNav = (item) => {
        handleBackToList();
    };

    const BASE_IMAGE_URL = ServiceProvider.assert.get;

    const getImageUrl = (item) => {
        if (!item.images || item.images.length === 0) {
            return null;
        }
        const firstImage = item.images[0];
        return `${BASE_IMAGE_URL}images/pdi_master/${firstImage}`;
    };

    return (
        <div className="print-container">
            <div className="print-sticky-header">
                <Breadcrumbs backNav={backNav} />
                {viewMode === 'docmaster' && (
                    <div className="print-header">
                        <div className="print-search-box">
                            <Search size={18} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Enter TPL Number or Description..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                className="search-input"
                            />
                            {searchTerm && (
                                <X
                                    size={16}
                                    className="search-clear"
                                    onClick={clearSearch}
                                />
                            )}
                        </div>

                        <button
                            className="print-search-btn"
                            onClick={handleSearch}
                            disabled={loading}
                        >
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                )}

                {viewMode === 'pdi' && (
                    <div className="print-pdi-header">
                        <ArrowLeft size={16} onClick={() => { removeCustomPath(); handleBackToList(); }} />
                        <div className="print-pdi-doc-info">
                            <h3>PDI Desc - {selectedDoc[`${viewMode}_desc`]}</h3>
                        </div>
                        {viewMode === 'pdi' && pdiItems.length > 0 && (
                            <button
                                className="print-bulk-print-btn"
                                onClick={handleBulkPrint}
                                disabled={selectedItems.length === 0}
                            >
                                Print Selected ({selectedItems.length})
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="print-scrollable-container">
                <div className="print-table-container">
                    <div className="print-table-wrapper">
                        {viewMode === 'docmaster' && (
                            <table className="print-table docmaster-table">
                                <thead>
                                    <tr>
                                        <th className="text-center">S NO</th>
                                        <th className="text-center">TPL NO</th>
                                        <th className="text-left">TPL DESC</th>
                                        <th className="text-center">PDI ITEMS</th>
                                        <th className="text-center">STATUS</th>
                                        <th className="text-center">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedDocMasters.map((doc, index) => (
                                        <tr key={doc._id}>
                                            <td className="text-center">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td className="text-center">{doc.tpl_no}</td>
                                            <td className="text-left">{doc.tpl_desc || 'N/A'}</td>
                                            <td className="text-center">{doc.pdi_items?.length || 0}</td>
                                            <td className="text-center">
                                                <span className={`status ${doc.status}`}>
                                                    {doc.status}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <div className="action-buttons">
                                                    <button
                                                        className={`btn-view ${doc.pdi_items?.length > 0 ? '' : 'disabled'}`}
                                                        onClick={() => handleViewPDI(doc)}
                                                        disabled={!doc.pdi_items || doc.pdi_items.length === 0}
                                                        title={doc.pdi_items?.length > 0 ? "View PDI Items" : "No PDI Items"}
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {viewMode === 'pdi' && (
                            <table className="print-table pdi-table">
                                <thead>
                                    <tr>
                                        <th className="text-center">
                                            <div className="checkbox-header">
                                                <button
                                                    className={`checkbox-btn ${selectAll ? 'checked' : ''}`}
                                                    onClick={handleSelectAll}
                                                >
                                                    {selectAll ? <CheckSquare size={16} /> : <Square size={16} />}
                                                </button>
                                            </div>
                                        </th>
                                        <th className="text-center">SNO</th>
                                        <th className="text-center">IMAGE</th>
                                        <th className="text-left">DESCRIPTION</th>
                                        <th className="text-center">PART NO</th>
                                        <th className="text-center">SIZE</th>
                                        <th className="text-left">TYPE OF PACKING</th>
                                        <th className="text-center">QUANTITY</th>
                                        <th className="text-center">BOX QUANTITY</th>
                                        <th className="text-center">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedPdiItems.map((item, index) => {
                                        const isSelected = selectedItems.some(selected => selected.id === item.id);
                                        const imageUrl = getImageUrl(item);
                                        return (
                                            <tr key={item.id} className={isSelected ? 'selected-row' : ''}>
                                                <td className="text-center">
                                                    <button
                                                        className={`checkbox-btn ${isSelected ? 'checked' : ''}`}
                                                        onClick={() => handleSelectItem(item)}
                                                    >
                                                        {isSelected ? <Check size={16} /> : <Square size={16} />}
                                                    </button>
                                                </td>
                                                <td className="text-center">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                {imageUrl ? (
                                                    <td className="text-center">
                                                        <div className="image-placeholder">
                                                            <SecureImage
                                                                url={imageUrl}
                                                                token={userData?.accessToken}
                                                                alt="Item"
                                                                className="item-image"
                                                            />
                                                        </div>
                                                    </td>
                                                ) : <td></td>}
                                                <td className="text-left description-cell">{item.desc || 'N/A'}</td>
                                                <td className="text-center partno-cell">{item.pn || 'N/A'}</td>
                                                <td className="text-center size-cell">{item.sz || 'N/A'}</td>
                                                <td className="text-left packing-text">{item.prt_txt || '-'}</td>
                                                <td className="text-center quantity-cell">{item.qty || 0}</td>
                                                <td className="text-center box-quantity-cell">{item.b_qty || 0}</td>
                                                <td className="text-center">
                                                    <div className="action-buttons">
                                                        <button
                                                            className="btn-qr-individual"
                                                            onClick={() => handleActionPrint(item)}
                                                            disabled={loading}
                                                            title="Print QR Sticker"
                                                        >
                                                            <QrCodeScannerIcon />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            <div className="print-pagination">
                <div className="print-rows-per-page">
                    <span>Rows per page : </span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                        disabled={loading}
                    >
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>

                <span className="print-page-info">
                    Showing {Math.min((currentPage - 1) * itemsPerPage + 1, viewMode === 'docmaster' ? docMasters.length : pdiItems.length)} to {Math.min(currentPage * itemsPerPage, viewMode === 'docmaster' ? docMasters.length : pdiItems.length)} of {viewMode === 'docmaster' ? docMasters.length : pdiItems.length} entries
                </span>

                <div className="print-pagination-controls">
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1 || loading}>⟪</button>
                    <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1 || loading}>⟨</button>
                    {docMasters.length > 0 ? (
                        <span className="location-page-numbers">Page {currentPage} of {totalPages}</span>
                    ) : (
                        <span className="location-page-numbers">Page 0 of 0</span>
                    )}
                    <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages || loading}>⟩</button>
                    <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages || loading}>⟫</button>
                </div>
            </div>

            {/* Individual Print Modal - kept for checkbox print */}
            {showPrint && (
                <div className="print-modal-overlay" onClick={() => {
                    setCount(1);
                    setShowPrint(null);
                    setPreviewTotal(0);
                }}>
                    <div className="print-modal" onClick={(e) => { e.stopPropagation(); }}>
                        <div className="print-modal-header">
                            <h3>Print QR Code</h3>
                            <button
                                className="modal-close-btn"
                                onClick={() => {
                                    setCount(1);
                                    setShowPrint(null);
                                    setPreviewTotal(0);
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="print-modal-content">
                            <div className="qr-preview-card">
                                <img src={qrImage} alt="QR" className="qr-image" />
                                <div className="qr-info">
                                    <div className="qr-info-row"><strong>{showPrint.desc}</strong></div>
                                    <div className="qr-info-row">Part No: {showPrint.pn}</div>
                                    <div className="qr-info-row">Size: {showPrint.sz}</div>
                                    <div className="qr-info-row">Total Quantity: {showPrint.qty}</div>
                                    <div className="qr-info-row">Box Quantity: {showPrint.b_qty || showPrint.qty}</div>
                                </div>
                            </div>

                            <div className="print-count-section" style={{ marginTop: '20px', padding: '15px', borderTop: '1px solid #eee' }}>
                                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                                    Number of Copies:
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <button
                                        onClick={() => handleCountChange(count - 1)}
                                        disabled={count <= 1}
                                        style={{ padding: '5px 10px', fontSize: '18px' }}
                                    >-</button>
                                    <input
                                        type="number"
                                        min="1"
                                        max="100"
                                        value={count}
                                        onChange={(e) => handleCountChange(e.target.value)}
                                        style={{ width: '60px', textAlign: 'center', padding: '5px' }}
                                    />
                                    <button
                                        onClick={() => handleCountChange(count + 1)}
                                        disabled={count >= 100}
                                        style={{ padding: '5px 10px', fontSize: '18px' }}
                                    >+</button>
                                </div>
                                <div style={{ marginTop: '10px', color: '#666', fontSize: '12px' }}>
                                    Total stickers to print: <strong>{previewTotal}</strong>
                                </div>
                            </div>

                            <div className="print-controls" style={{ marginTop: '20px' }}>
                                <button
                                    className="btn-print-modal"
                                    onClick={() => {
                                        generateAndPrintQRCards([showPrint], count);
                                        setShowPrint(null);
                                        setPreviewTotal(0);
                                        setCount(1);
                                    }}
                                >
                                    <Printer size={16} style={{ marginRight: '5px' }} />
                                    Print {previewTotal} Stickers
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Print Modal - used for both bulk and action print */}
            {showBulkPrint && (
                <div className="print-modal-overlay" onClick={() => {
                    setCount(1);
                    setShowBulkPrint(false);
                    setPreviewTotal(0);
                    setSelectedItems([]);
                    setActionItem(null);
                    setActionBoxDistribution(null);
                }}>
                    <div className="print-modal bulk-print-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="print-modal-header">
                            <h3>
                                {actionItem
                                    ? `Print QR Code - ${actionItem.pn || actionItem.desc}`
                                    : selectedItems.length === 1
                                        ? `Print QR Code - ${selectedItems[0].pn || selectedItems[0].desc}`
                                        : 'Bulk Print QR Codes'}
                            </h3>
                            <button
                                className="modal-close-btn"
                                onClick={() => {
                                    setCount(1);
                                    setShowBulkPrint(false);
                                    setPreviewTotal(0);
                                    setSelectedItems([]);
                                    setActionItem(null);
                                    setActionBoxDistribution(null);
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="print-modal-content">
                            <div className="bulk-print-info">
                                <div className="selected-items-list" style={{ maxHeight: '200px', overflow: 'auto', marginBottom: '15px' }}>
                                    {actionItem ? (
                                        <div className="selected-item" style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span>1. {actionItem.desc} - {actionItem.pn}</span>
                                                <span style={{ color: '#666' }}>SN: {actionItem.sn}</span>
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                                Qty: {actionItem.qty}, Box: {actionItem.b_qty || actionItem.qty}
                                                {actionBoxDistribution && (
                                                    <span style={{ color: '#ff8c00', fontWeight: 'bold', marginLeft: '10px' }}>
                                                        (Printing: {actionBoxDistribution[0].qty} pcs per sticker)
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        selectedItems.map((item, index) => (
                                            <div key={item.id} className="selected-item" style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>{index + 1}. {item.desc} - {item.pn}</span>
                                                    <span style={{ color: '#666' }}>SN: {item.sn}</span>
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                                    Qty: {item.qty}, Box: {item.b_qty || item.qty}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="print-count-section" style={{ padding: '15px', borderTop: '2px solid #eee', borderBottom: '2px solid #eee', marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>
                                    Number of Copies:
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                                    <button
                                        className="btn-count-adjust"
                                        onClick={() => handleCountChange(count - 1)}
                                        disabled={count <= 1}
                                        style={{
                                            padding: '8px 15px',
                                            fontSize: '20px',
                                            border: '1px solid #ddd',
                                            background: '#f8f9fa',
                                            borderRadius: '5px',
                                            cursor: count <= 1 ? 'not-allowed' : 'pointer'
                                        }}
                                    >-</button>
                                    <input
                                        type="number"
                                        min="1"
                                        max="100"
                                        value={count}
                                        onChange={(e) => handleCountChange(e.target.value)}
                                        style={{
                                            width: '80px',
                                            textAlign: 'center',
                                            padding: '8px',
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            border: '2px solid #4a90e2',
                                            borderRadius: '5px'
                                        }}
                                    />
                                    <button
                                        className="btn-count-adjust"
                                        onClick={() => handleCountChange(count + 1)}
                                        disabled={count >= 100}
                                        style={{
                                            padding: '8px 15px',
                                            fontSize: '20px',
                                            border: '1px solid #ddd',
                                            background: '#f8f9fa',
                                            borderRadius: '5px',
                                            cursor: count >= 100 ? 'not-allowed' : 'pointer'
                                        }}
                                    >+</button>
                                </div>
                                <div style={{ marginTop: '10px', textAlign: 'center', color: '#666', fontSize: '13px' }}>
                                    Total stickers to print: <strong style={{ color: '#4a90e2', fontSize: '16px' }}>{previewTotal}</strong>
                                </div>
                            </div>

                            <div className="print-controls">
                                <div className="bulk-print-actions"  >
                                    <button
                                        className="btn-print-cancel"
                                        onClick={() => {
                                            setShowBulkPrint(false);
                                            setPreviewTotal(0);
                                            setCount(1);
                                            setSelectedItems([]);
                                            setActionItem(null);
                                            setActionBoxDistribution(null);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn-print-modal"
                                        onClick={actionItem ? handleActionPrintExecute : handleExecuteBulkPrint}
                                    >
                                        Print {previewTotal} Stickers
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Box Selection Modal - with count input */}
            {showBoxSelection && boxSelectionItem && (
                <div className="print-modal-overlay" onClick={() => {
                    setShowBoxSelection(false);
                    setBoxSelectionItem(null);
                    setSelectedBoxTypes([]);
                    setBoxSelectionCount(1);
                }}>
                    <div className="print-modal box-selection-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="print-modal-header">
                            <h3>Select Box Type to Print</h3>
                            <button
                                className="modal-close-btn"
                                onClick={() => {
                                    setShowBoxSelection(false);
                                    setBoxSelectionItem(null);
                                    setSelectedBoxTypes([]);
                                    setBoxSelectionCount(1);
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="print-modal-content">
                            <div className="box-selection-info">
                                <div style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                        {boxSelectionItem.desc} - {boxSelectionItem.pn}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#666' }}>
                                        Total Qty: {boxSelectionItem.qty} | Box Qty: {boxSelectionItem.b_qty || boxSelectionItem.qty}
                                    </div>
                                </div>

                                <div className="box-cards-container">
                                    {selectedBoxTypes.map((box) => (
                                        <div
                                            key={box.id}
                                            onClick={() => toggleBoxTypeSelection(box.id)}
                                            className="box-card-wrapper"
                                            style={{ position: 'relative', cursor: 'pointer' }}
                                        >
                                            <div
                                                className="box-selection-badge"
                                                style={{
                                                    position: 'absolute',
                                                    top: '10px',
                                                    right: '10px',
                                                    zIndex: 2,
                                                    backgroundColor: box.selected ? '#4caf50' : '#e0e0e0',
                                                    width: '28px',
                                                    height: '28px',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    boxShadow: box.selected ? '0 2px 8px rgba(76, 175, 80, 0.4)' : 'none',
                                                    transition: 'all 0.2s ease',
                                                    border: box.selected ? '2px solid white' : '2px solid #f0f0f0'
                                                }}
                                            >
                                                {box.selected && <Check size={16} />}
                                            </div>

                                            <div
                                                className="qr-preview-card box-card-preview"
                                                style={{
                                                    borderColor: box.selected ? '#4caf50' : '#e2e2e2',
                                                    borderWidth: box.selected ? '3px' : '1px',
                                                    borderStyle: 'solid',
                                                    transition: 'all 0.2s ease',
                                                    marginBottom: '0'
                                                }}
                                            >
                                                <img src={qrImage} alt="QR" className="qr-image" />
                                                <div className="qr-info">
                                                    <div className="qr-info-row" style={{ fontWeight: 'bold', color: '#333' }}>{boxSelectionItem.desc}</div>
                                                    <div className="qr-info-row">Part No: {boxSelectionItem.pn}</div>
                                                    <div className="qr-info-row">Size: {boxSelectionItem.sz}</div>
                                                    <div className="qr-info-row highlight-qty" style={{ color: '#ff8c00', fontWeight: 'bold' }}>
                                                        Quantity: {box.qty} pcs
                                                    </div>
                                                    <div className="qr-info-row" style={{ fontSize: '11px', color: '#666' }}>
                                                        {box.description}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Count Input for Box Selection */}
                                <div className="print-count-section" style={{ padding: '15px', borderTop: '2px solid #eee', marginTop: '15px' }}>
                                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>
                                        Number of Copies:
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                                        <button
                                            onClick={() => handleBoxSelectionCountChange(boxSelectionCount - 1)}
                                            disabled={boxSelectionCount <= 1}
                                            style={{
                                                padding: '8px 15px',
                                                fontSize: '20px',
                                                border: '1px solid #ddd',
                                                background: '#f8f9fa',
                                                borderRadius: '5px',
                                                cursor: boxSelectionCount <= 1 ? 'not-allowed' : 'pointer'
                                            }}
                                        >-</button>
                                        <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={boxSelectionCount}
                                            onChange={(e) => handleBoxSelectionCountChange(e.target.value)}
                                            style={{
                                                width: '80px',
                                                textAlign: 'center',
                                                padding: '8px',
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                border: '2px solid #4a90e2',
                                                borderRadius: '5px'
                                            }}
                                        />
                                        <button
                                            onClick={() => handleBoxSelectionCountChange(boxSelectionCount + 1)}
                                            disabled={boxSelectionCount >= 100}
                                            style={{
                                                padding: '8px 15px',
                                                fontSize: '20px',
                                                border: '1px solid #ddd',
                                                background: '#f8f9fa',
                                                borderRadius: '5px',
                                                cursor: boxSelectionCount >= 100 ? 'not-allowed' : 'pointer'
                                            }}
                                        >+</button>
                                    </div>
                                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#666', fontSize: '13px' }}>
                                        Total stickers to print: <strong style={{ color: '#4a90e2', fontSize: '16px' }}>{boxSelectionCount}</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="print-controls">
                                <div className="bulk-print-actions">
                                    <button
                                        className="btn-print-cancel"
                                        onClick={() => {
                                            setShowBoxSelection(false);
                                            setBoxSelectionItem(null);
                                            setSelectedBoxTypes([]);
                                            setBoxSelectionCount(1);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn-print-modal"
                                        onClick={handlePrintFromBoxSelection}
                                        disabled={!selectedBoxTypes.some(box => box.selected)}
                                    >
                                        Print {boxSelectionCount} Sticker{boxSelectionCount > 1 ? 's' : ''}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrintScreen;